import type { MaterialType } from '@slimebound/core';
import {
  MeshBuilder,
  StandardMaterial,
  Color3,
  type Scene,
  type AbstractMesh,
  type ShadowGenerator
} from '@babylonjs/core';

export interface WorldPickupItem {
  id: string;
  name: string;
  materialType: MaterialType;
  massKg: number;
  gelEnergyYield: number;
  biomassYield: number;
  position: { x: number; y: number; z: number };
  radius: number;
  mesh?: AbstractMesh;
}

export class PickupManager {
  private pickups: WorldPickupItem[] = [];
  private animationTime: number = 0;

  public addPickup(pickup: WorldPickupItem): void {
    this.pickups.push(pickup);
  }

  public getActivePickups(): readonly WorldPickupItem[] {
    return this.pickups;
  }

  public checkProximity(
    slimePos: { x: number; y: number; z: number },
    slimeRadius: number
  ): WorldPickupItem[] {
    const absorbed: WorldPickupItem[] = [];
    const remaining: WorldPickupItem[] = [];

    for (const pickup of this.pickups) {
      const dx = slimePos.x - pickup.position.x;
      const dy = slimePos.y - pickup.position.y;
      const dz = slimePos.z - pickup.position.z;
      const distance = Math.hypot(dx, dy, dz);

      if (distance <= slimeRadius + pickup.radius + 0.3) {
        if (pickup.mesh) {
          pickup.mesh.dispose();
        }
        absorbed.push(pickup);
      } else {
        remaining.push(pickup);
      }
    }

    this.pickups = remaining;
    return absorbed;
  }

  public spawnWorldPickups(scene: Scene, shadowGenerator?: ShadowGenerator): void {
    const definitions: Array<{
      name: string;
      materialType: MaterialType;
      massKg: number;
      energy: number;
      biomass: number;
      color: Color3;
      emissive: Color3;
      count: number;
    }> = [
      {
        name: 'Vitality Herb',
        materialType: 'organic',
        massKg: 1.2,
        energy: 25,
        biomass: 1.2,
        color: new Color3(0.2, 0.85, 0.3),
        emissive: new Color3(0.05, 0.25, 0.08),
        count: 4
      },
      {
        name: 'Iron Ore Chunk',
        materialType: 'mineral',
        massKg: 3.5,
        energy: 5,
        biomass: 0.2,
        color: new Color3(0.65, 0.7, 0.75),
        emissive: new Color3(0.1, 0.12, 0.15),
        count: 4
      },
      {
        name: 'Mana Essence Crystal',
        materialType: 'essence',
        massKg: 0.8,
        energy: 50,
        biomass: 0.5,
        color: new Color3(0.75, 0.2, 0.95),
        emissive: new Color3(0.3, 0.05, 0.4),
        count: 3
      }
    ];

    let idCounter = 1;
    for (const def of definitions) {
      const mat = new StandardMaterial(`pickupMat_${def.name}`, scene);
      mat.diffuseColor = def.color;
      mat.emissiveColor = def.emissive;
      mat.specularPower = 64;

      for (let i = 0; i < def.count; i++) {
        const id = `pickup_${idCounter++}`;
        const angle = Math.random() * Math.PI * 2;
        const dist = 5 + Math.random() * 18;
        const x = Math.cos(angle) * dist;
        const z = Math.sin(angle) * dist;
        const y = 0.5;

        const mesh = MeshBuilder.CreateSphere(id, { diameter: 0.7, segments: 16 }, scene);
        mesh.position.set(x, y, z);
        mesh.material = mat;

        if (shadowGenerator) {
          shadowGenerator.addShadowCaster(mesh);
        }

        this.addPickup({
          id,
          name: def.name,
          materialType: def.materialType,
          massKg: def.massKg,
          gelEnergyYield: def.energy,
          biomassYield: def.biomass,
          position: { x, y, z },
          radius: 0.35,
          mesh
        });
      }
    }
  }

  public update(dt: number): void {
    this.animationTime += dt;
    for (const pickup of this.pickups) {
      if (pickup.mesh) {
        pickup.mesh.rotation.y += 1.5 * dt;
        pickup.mesh.position.y = 0.5 + Math.sin(this.animationTime * 2.5 + pickup.position.x) * 0.12;
      }
    }
  }
}
